// components/clients/ClientDetailScreen.jsx
import React from 'react';
import { Alert, Button, Empty, Spin } from 'antd';
import { ArrowLeftOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { ClientMetricGrid } from './ClientMetricGrid';
import { RelaunchPanel } from './RelaunchPanel';
import { formatDate, integer, money } from '../../utils/mobileHelpers';
import { buildWaLink, normalizePhoneCd } from '../../../../utils/whatsapp';

const RecentSales = ({ sales }) => (
  <section className="mobile-client-detail-panel">
    <span>DERNIÈRES VENTES</span>
    {sales?.length ? (
      sales.map((sale) => {
        const variant = [sale.couleur, sale.taille ? `P${sale.taille}` : null].filter(Boolean).join(' · ');
        return (
          <div className="mobile-client-detail-row" key={sale.id_vente}>
            <div>
              <strong>{sale.nom_produit || 'Produit'}</strong>
              <small>{variant || formatDate(sale.date_vente)}</small>
            </div>
            <b>{money(sale.amount)}</b>
          </div>
        );
      })
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune vente confirmée" />
    )}
  </section>
);

const RecentOrders = ({ orders }) => (
  <section className="mobile-client-detail-panel">
    <span>DERNIÈRES COMMANDES</span>
    {orders?.length ? (
      orders.map((order) => (
        <div className="mobile-client-detail-row" key={order.id_commande}>
          <div>
            <strong>Commande #{order.id_commande}</strong>
            <small>{`${formatDate(order.date_commande)} · ${integer(order.pairs)} paire(s)`}</small>
          </div>
          <b>{money(order.amount)}</b>
        </div>
      ))
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune commande" />
    )}
  </section>
);

// Fidélité + contact : solde de points (barème et expiration gérés côté serveur)
// et message WhatsApp pré-rempli (lien wa.me — envoi manuel, sans API tierce).
const LoyaltyContactPanel = ({ selected, loyalty }) => {
  const name = selected.client_name || 'Chère cliente';
  const balance = Number(loyalty?.points_balance);
  const points = Number.isFinite(balance) ? balance : null;
  const expiry = loyalty?.next_expiry ? formatDate(loyalty.next_expiry) : null;
  const hasPhone = Boolean(normalizePhoneCd(selected.client_phone));

  const message = points && points > 0
    ? `Bonjour ${name}, merci pour votre fidélité chez Ndoé Boutique ! Vous cumulez ${points} point(s)${expiry ? `, à utiliser avant le ${expiry}` : ''}. À très bientôt.`
    : `Bonjour ${name}, merci de votre confiance chez Ndoé Boutique. À très bientôt !`;
  const href = hasPhone ? buildWaLink(selected.client_phone, message) : null;

  return (
    <section className="mobile-client-detail-panel">
      <span>FIDÉLITÉ &amp; CONTACT</span>
      <div className="mobile-client-detail-row">
        <div>
          <strong>{points !== null ? `${points} point(s) de fidélité` : 'Programme fidélité non activé'}</strong>
          <small>{expiry ? `Prochaine expiration : ${expiry}` : 'Points cumulés selon le barème Ndoé'}</small>
        </div>
        {href ? (
          <Button type="primary" ghost icon={<WhatsAppOutlined />} href={href} target="_blank" rel="noopener noreferrer">
            WhatsApp
          </Button>
        ) : (
          <small>Téléphone non renseigné</small>
        )}
      </div>
    </section>
  );
};

// Fiche cliente : header, métriques cumulées, pointures, relance, ventes et commandes récentes.
export const ClientDetailScreen = ({ selected, detail, onBack, loyalty }) => {
  const metrics = [
    { label: 'Paires nettes', value: integer(selected.lifetime_net_pairs) },
    { label: 'Commandes', value: integer(selected.lifetime_orders) },
    { label: 'Valeur nette', value: money(selected.lifetime_net_amount) },
    { label: 'Dernier achat', value: formatDate(selected.last_sale) },
  ];

  return (
    <>
      <header className="mobile-clients-header">
        <Button shape="circle" icon={<ArrowLeftOutlined />} onClick={onBack} />
        <div>
          <span>NDOÉ MOBILE · CLIENTE</span>
          <h1>{selected.client_name || 'Cliente'}</h1>
          <p>{selected.client_phone || 'Téléphone non renseigné'}</p>
        </div>
      </header>
      {detail.loading || (!detail.data && !detail.error) ? (
        <div className="mobile-clients-loading">
          <Spin size="large" />
          <span>Chargement de la fiche cliente…</span>
        </div>
      ) : detail.error ? (
        <Alert showIcon type="warning" message={detail.error} />
      ) : (
        <>
          <ClientMetricGrid metrics={metrics} />
          <LoyaltyContactPanel selected={selected} loyalty={loyalty} />
          {selected.preferred_sizes?.length > 0 && (
            <section className="mobile-client-detail-panel">
              <span>POINTURES HABITUELLES</span>
              <h2>{selected.preferred_sizes.map((size) => `P${size}`).join(' · ')}</h2>
            </section>
          )}
          <RelaunchPanel client={selected} />
          <RecentSales sales={detail.data?.recent_sales} />
          <RecentOrders orders={detail.data?.recent_orders} />
        </>
      )}
    </>
  );
};
